/**
 * Tab-2 annotator for the выкладка (empty_shelf) fullness-% model.
 *
 * Sibling of CropAnnotator, but the label is a single free number 0-100 (shelf
 * fullness %), not the table booleans. All crops render at once; a focus cursor
 * moves with the arrows. Fast entry: digit keys 0-9 set deciles (5 → 50%) and
 * auto-advance; F sets 100 (полная), E sets 0 (пустая); a slider + number input
 * on the focused card give precise values; Space/Enter advances.
 *
 * The local annotations.json (server-side copy) is the source of truth: edits
 * debounce-save via annotateShelfBulk; "Залить в ClearML" ships it as a new
 * version (and, for the declined dataset, auto-distributes to per-zone).
 */
import { Select } from '@/components/ui/select';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useSWR, { mutate as globalMutate } from 'swr';
import { Loader2, UploadCloud, Trash2, CheckCheck, ZoomIn, ZoomOut } from 'lucide-react';
import { useT } from '@/contexts/I18nContext';
import { API_BASE } from '@/api/base';
import {
  getDownload, getShelfCrops, annotateShelfBulk, uploadNewVersion, deleteLocalCopy,
  deleteShelfCrop, type ShelfCropItem, type ShelfLabelDelta,
} from '@/api/datasets';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { layoutSafeLetter } from '@/utils/keyboardLayout';
import { Tooltip } from '@/components/ui/tooltip';

const MIN_COLS = 2;
const MAX_COLS = 8;
const clampPct = (v: number) => Math.max(0, Math.min(100, Math.round(v)));
const keyOf = (it: { shelf_id: number; frame_id: number }) => `${it.shelf_id}:${it.frame_id}`;

// Outline color by fill level (empty → full): rose → amber → emerald.
function fillOutline(pct: number | null | undefined): string {
  if (pct == null) return 'outline outline-4 outline-slate-300';
  if (pct < 25) return 'outline outline-4 outline-rose-500';
  if (pct < 70) return 'outline outline-4 outline-amber-500';
  return 'outline outline-4 outline-emerald-500';
}

interface Props { downloadId: number; onClose?: () => void; }

export default function ShelfPercentageAnnotator({ downloadId, onClose }: Props) {
  const { t } = useT();
  const [submitting, setSubmitting] = useState(false);
  const [src, setSrc] = useState<'all' | 'human' | 'ai'>('all');
  const [shelfFilter, setShelfFilter] = useState<number | 'all'>('all');
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const [deletedKeys, setDeletedKeys] = useState<Set<string>>(new Set());
  const [focus, setFocus] = useState(0);
  const [saved, setSaved] = useState('');
  const [cols, setCols] = useState(3);
  const [desc, setDesc] = useState('');

  // 1. Wait for the download to be ready.
  const { data: dl } = useSWR(
    ['mml-download', downloadId],
    () => getDownload(downloadId).then(r => r.data),
    { refreshInterval: (d) => (d?.status === 'ready' || d?.status === 'failed' ? 0 : 2000) },
  );
  const ready = dl?.status === 'ready';

  // 2. Load ALL crops once when ready.
  const { data: cropsResp } = useSWR(
    ready ? ['mml-shelf-crops', downloadId] : null,
    () => getShelfCrops(downloadId).then(r => r.data),
    { revalidateOnFocus: false },
  );

  // 3. Local label state (percentage per crop; source of truth for the session).
  const [pct, setPct] = useState<Record<string, number | null>>({});
  useEffect(() => {
    if (!cropsResp) return;
    const init: Record<string, number | null> = {};
    for (const it of cropsResp.items) init[keyOf(it)] = it.percentage;
    setPct(init);
  }, [cropsResp]);

  const items: ShelfCropItem[] = useMemo(() => cropsResp?.items ?? [], [cropsResp]);

  const shelfOptions = useMemo(() => {
    const m = new Map<number, { id: number; name: string; count: number }>();
    for (const it of items) {
      if (deletedKeys.has(keyOf(it))) continue;
      const e = m.get(it.shelf_id);
      if (e) e.count++;
      else m.set(it.shelf_id, { id: it.shelf_id, name: it.shelf ?? `s${it.shelf_id}`, count: 1 });
    }
    return [...m.values()].sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
  }, [items, deletedKeys]);

  const isHuman = useCallback(
    (it: ShelfCropItem) => it.source === 'user' || touched.has(keyOf(it)),
    [touched],
  );

  const visible = useMemo(() => {
    let list = items.filter(it => !deletedKeys.has(keyOf(it)));
    if (shelfFilter !== 'all') list = list.filter(it => it.shelf_id === shelfFilter);
    if (src === 'human') list = list.filter(isHuman);
    else if (src === 'ai') list = list.filter(it => !isHuman(it));
    return list;
  }, [items, src, isHuman, deletedKeys, shelfFilter]);

  useEffect(() => { setFocus(f => Math.min(f, Math.max(0, visible.length - 1))); }, [visible.length]);

  // ── debounced bulk save ────────────────────────────────────────────────────
  const dirty = useRef<Set<string>>(new Set());
  const pctRef = useRef(pct);
  pctRef.current = pct;
  const itemByKey = useMemo(() => {
    const m: Record<string, ShelfCropItem> = {};
    for (const it of items) m[keyOf(it)] = it;
    return m;
  }, [items]);
  const saveTimer = useRef<number | null>(null);

  const flush = useCallback(async () => {
    if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null; }
    if (!dirty.current.size) return;
    const keys = [...dirty.current];
    dirty.current.clear();
    const deltas: ShelfLabelDelta[] = keys.map(k => {
      const it = itemByKey[k];
      const v = pctRef.current[k];
      return { shelf_id: it.shelf_id, frame_id: it.frame_id, ...(v == null ? {} : { percentage: v }) };
    });
    try {
      const r = await annotateShelfBulk(downloadId, deltas);
      setSaved(`✓ ${r.data.saved}`);
      window.setTimeout(() => setSaved(s => (s.startsWith('✓') ? '' : s)), 1500);
    } catch {
      setSaved('save failed');
      keys.forEach(k => dirty.current.add(k));
    }
  }, [downloadId, itemByKey]);

  const scheduleSave = useCallback((k: string) => {
    dirty.current.add(k);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(flush, 300);
  }, [flush]);

  useEffect(() => () => { void flush(); }, [flush]);

  const setValue = useCallback((it: ShelfCropItem, value: number) => {
    const k = keyOf(it);
    setPct(prev => ({ ...prev, [k]: clampPct(value) }));
    setTouched(prev => (prev.has(k) ? prev : new Set(prev).add(k)));
    scheduleSave(k);
  }, [scheduleSave]);

  const advance = useCallback(() => {
    setFocus(f => Math.min(f + 1, Math.max(0, visible.length - 1)));
  }, [visible.length]);

  const move = useCallback((dir: 'left' | 'right' | 'up' | 'down') => {
    setFocus(f => {
      const n = visible.length;
      if (!n) return 0;
      if (dir === 'right') return Math.min(n - 1, f + 1);
      if (dir === 'left') return Math.max(0, f - 1);
      if (dir === 'down') return Math.min(n - 1, f + cols);
      return Math.max(0, f - cols);
    });
  }, [visible.length, cols]);

  // ── keyboard: digits = deciles + advance; F=100, E=0; Space/Enter advances ──
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'TEXTAREA' || tag === 'INPUT') return; // don't hijack while typing
      if (e.key === 'ArrowRight') { e.preventDefault(); move('right'); return; }
      if (e.key === 'ArrowLeft') { e.preventDefault(); move('left'); return; }
      if (e.key === 'ArrowDown') { e.preventDefault(); move('down'); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); move('up'); return; }
      const it = visible[focus];
      if (!it) return;
      if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); advance(); return; }
      // Digits stay on `key` — they are identical in every layout we support, and
      // reading them off `code` would break the numpad. Letters go through the
      // physical-key helper so «а»/«у» work as F/E (utils/keyboardLayout).
      if (/^[0-9]$/.test(e.key)) { e.preventDefault(); setValue(it, Number(e.key) * 10); advance(); return; }
      const k = layoutSafeLetter(e);
      if (k === 'f') { e.preventDefault(); setValue(it, 100); advance(); return; } // full / полная
      if (k === 'e') { e.preventDefault(); setValue(it, 0); advance(); return; }   // empty / пустая
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [focus, visible, move, advance, setValue]);

  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  useEffect(() => {
    cardRefs.current[focus]?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [focus, visible]);

  // ── actions ────────────────────────────────────────────────────────────────
  const upload = async () => {
    await flush();
    if (!confirm(t('data.annotate.uploadConfirm'))) return;
    setSubmitting(true);
    try { await uploadNewVersion(downloadId, desc.trim()); setSaved(t('data.annotate.uploaded')); }
    finally { setSubmitting(false); }
  };
  const markReviewed = async () => {
    const list = visible;
    if (!list.length) return;
    if (!confirm(t('data.annotate.reviewConfirm').replace('{n}', String(list.length)))) return;
    setSubmitting(true);
    try {
      await flush();
      const deltas: ShelfLabelDelta[] = list.map(it => ({ shelf_id: it.shelf_id, frame_id: it.frame_id }));
      await annotateShelfBulk(downloadId, deltas, 'user');
      setTouched(prev => { const n = new Set(prev); list.forEach(it => n.add(keyOf(it))); return n; });
      setSaved(t('data.annotate.reviewedDone').replace('{n}', String(list.length)));
    } finally { setSubmitting(false); }
  };
  const deletePhoto = async (it: ShelfCropItem) => {
    if (!confirm(t('data.annotate.deletePhotoConfirm'))) return;
    try {
      await deleteShelfCrop(downloadId, it.shelf_id, it.frame_id);
      setDeletedKeys(prev => new Set(prev).add(keyOf(it)));
      setSaved(t('data.annotate.photoDeleted'));
    } catch { setSaved('delete failed'); }
  };
  const remove = async () => {
    if (!confirm(t('data.annotate.deleteConfirm'))) return;
    setSubmitting(true);
    try {
      await deleteLocalCopy(downloadId);
      await Promise.all([globalMutate('mml-hierarchy'), globalMutate('mml-active-downloads')]);
      onClose?.();
    } finally { setSubmitting(false); }
  };

  const labelledCount = useMemo(
    () => visible.filter(it => pct[keyOf(it)] != null).length, [visible, pct]);

  if (!dl) return <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />;
  if (dl.status === 'failed')
    return <div className="text-sm text-destructive">{t('data.annotate.failed')}: {dl.status_detail}</div>;
  if (!ready) {
    const elapsed = dl.downloaded_at
      ? Math.max(0, Math.round((Date.now() - new Date(dl.downloaded_at).getTime()) / 1000)) : 0;
    return (
      <div className="mx-auto mt-10 max-w-md space-y-2 rounded-md border p-4">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Loader2 className="h-4 w-4 animate-spin" /> {t('data.annotate.downloading')}
        </div>
        <div className="relative h-2 w-full overflow-hidden rounded bg-muted">
          <div className="progress-indeterminate bg-primary" />
        </div>
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>{dl.status_detail || `${dl.status}…`}</span><span>{elapsed}s</span>
        </div>
      </div>
    );
  }
  if (!cropsResp) return <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />;
  if (!cropsResp.total) return <div className="text-sm text-muted-foreground">{t('data.annotate.noCrops')}</div>;

  return (
    <div className="space-y-3">
      <div className="sticky top-0 z-10 flex flex-wrap items-center gap-3 rounded-md border bg-background/95 px-3 py-2 backdrop-blur">
        <span className="text-sm font-medium">{t('data.annotate.shelfPct.title')}</span>
        <div className="inline-flex items-center gap-1">
          <Button variant="outline" size="icon" className="h-7 w-7" title={t('data.annotate.zoomIn')}
            disabled={cols <= MIN_COLS} onClick={() => setCols(c => Math.max(MIN_COLS, c - 1))}>
            <ZoomIn className="h-4 w-4" />
          </Button>
          <span className="w-4 text-center text-xs text-muted-foreground">{cols}</span>
          <Button variant="outline" size="icon" className="h-7 w-7" title={t('data.annotate.zoomOut')}
            disabled={cols >= MAX_COLS} onClick={() => setCols(c => Math.min(MAX_COLS, c + 1))}>
            <ZoomOut className="h-4 w-4" />
          </Button>
        </div>
        <div className="inline-flex overflow-hidden rounded-md border text-xs">
          {(['all', 'human', 'ai'] as const).map(s => (
            <button key={s} className={`px-2.5 py-1 ${src === s ? 'bg-secondary text-secondary-foreground' : 'text-muted-foreground'}`}
              onClick={() => setSrc(s)}>{t(`data.annotate.filter.${s}`)}</button>
          ))}
        </div>
        <Select className="h-7 text-xs" wrapperClassName="w-auto" value={shelfFilter}
          title={t('data.annotate.shelfPct.shelfFilter')}
          onChange={e => setShelfFilter(e.target.value === 'all' ? 'all' : Number(e.target.value))}>
          <option value="all">{t('data.annotate.tableFilter.all')} ({shelfOptions.reduce((n, o) => n + o.count, 0)})</option>
          {shelfOptions.map(o => (<option key={o.id} value={o.id}>🧺 {o.name} ({o.count})</option>))}
        </Select>
        <span className="text-xs text-muted-foreground">
          <kbd className="rounded bg-muted px-1">0-9</kbd> {t('data.annotate.shelfPct.deciles')} ·{' '}
          <kbd className="rounded bg-muted px-1">E</kbd> 0% · <kbd className="rounded bg-muted px-1">F</kbd> 100% ·{' '}
          <kbd className="rounded bg-muted px-1">Space</kbd> {t('data.annotate.next')}
        </span>
        <div className="ml-auto flex items-center gap-3">
          <span className="text-xs text-emerald-600">{saved}</span>
          <span className="text-xs text-muted-foreground">
            {Math.min(focus + 1, visible.length)} / {visible.length} · {labelledCount} {t('data.annotate.shelfPct.labelled')}
          </span>
          <Button variant="secondary" size="sm" disabled={submitting || !visible.length} onClick={markReviewed}
            title={t('data.annotate.reviewHint')}>
            <CheckCheck className="mr-1 h-4 w-4" /> {t('data.annotate.markReviewed')} ({visible.length})
          </Button>
          <Button variant="outline" size="sm" disabled={submitting} onClick={remove}>
            <Trash2 className="mr-1 h-4 w-4" /> {t('data.annotate.deleteLocal')}
          </Button>
          <Button size="sm" disabled={submitting} onClick={upload}>
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="mr-1 h-4 w-4" />}
            {t('data.annotate.upload')}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <p className="text-xs text-muted-foreground">{t('data.annotate.shelfPct.hint')}</p>
        {cropsResp.dataset && (
          <span className="text-xs text-muted-foreground">
            · 📍 {t('data.annotate.point')}:{' '}
            <span className="font-medium text-foreground">{cropsResp.dataset.zone_name ?? cropsResp.dataset.name}</span>
            {' · '}{t('data.annotate.version')}:{' '}
            <span className="font-mono">{cropsResp.dataset.version ?? cropsResp.dataset.version_id.slice(0, 8)}</span>
          </span>
        )}
      </div>

      <div className="rounded-md border bg-muted/20 p-3">
        <label htmlFor="ann-desc" className="mb-1 block text-sm font-medium">{t('data.annotate.descLabel')}</label>
        <p className="mb-1.5 text-xs text-muted-foreground">{t('data.annotate.descHelp')}</p>
        <Textarea id="ann-desc" value={desc} onChange={e => setDesc(e.target.value)} rows={2}
          placeholder={t('data.annotate.descPlaceholder')} className="resize-y text-sm" />
      </div>

      <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
        {visible.map((it, i) => {
          const k = keyOf(it);
          const v = pct[k];
          const isFocus = i === focus;
          return (
            <Tooltip key={k} content={`shelf ${it.shelf_id} · frame ${it.frame_id}`}><div ref={el => { cardRefs.current[i] = el; }} onClick={() => setFocus(i)}
              className={`group relative cursor-pointer rounded-lg bg-white p-1 ${fillOutline(v)} ${
                isFocus ? 'z-10 scale-[1.03] shadow-xl ring-[6px] ring-blue-600 ring-offset-2' : 'opacity-95 hover:opacity-100'
              }`}>
              {isHuman(it) && (
                <Tooltip content={t('data.annotate.humanBadge')}><span className="absolute right-1.5 top-1.5 z-10 flex items-center gap-0.5 rounded bg-blue-600/90 px-1 py-0.5 text-[9px] font-semibold uppercase text-white"><CheckCheck className="h-3 w-3" /></span></Tooltip>
              )}
              <Tooltip content={t('data.annotate.deletePhoto')}><button className="absolute left-1.5 top-1.5 z-10 rounded bg-rose-600/90 p-1 text-white opacity-0 transition group-hover:opacity-100 hover:bg-rose-700"
                aria-label={t('data.annotate.deletePhoto')} onClick={(e) => { e.stopPropagation(); deletePhoto(it); }}>
                <Trash2 className="h-3.5 w-3.5" />
              </button></Tooltip>
              <img src={`${API_BASE}${it.crop_url}`} loading="lazy" alt={`s${it.shelf_id} f${it.frame_id}`}
                className="block h-auto max-h-[600px] w-full rounded bg-black/5 object-contain" />
              <div className="mt-1 flex items-center justify-between gap-1 text-[11px] text-muted-foreground">
                <Tooltip content={it.camera ?? ''}><span className="truncate">📷 {it.camera ?? `cam ${it.camera_id ?? '?'}`}</span></Tooltip>
                <span className={`rounded px-1.5 font-semibold ${v == null ? 'bg-slate-200 text-slate-600' : 'bg-emerald-100 text-emerald-800'}`}>
                  {v == null ? '—' : `${v}%`}
                </span>
              </div>
              {/* precise entry on the focused card */}
              {isFocus && (
                <div className="mt-1 flex items-center gap-2" onClick={e => e.stopPropagation()}>
                  <input type="range" min={0} max={100} step={1} value={v ?? 0}
                    onChange={e => setValue(it, Number(e.target.value))} className="h-4 flex-1" />
                  <input type="number" min={0} max={100} value={v ?? ''} placeholder="—"
                    onChange={e => { const n = e.target.value; if (n !== '') setValue(it, Number(n)); }}
                    className="w-14 rounded border bg-background px-1 py-0.5 text-xs" />
                </div>
              )}
            </div></Tooltip>
          );
        })}
      </div>
    </div>
  );
}
